import React, { createContext, useEffect, useState, useCallback } from "react";
import AuthenticationService from "../services/AuthenticationService";
import WalletService from "../services/WalletService";
import { isSupportedCurrency } from "../constants/currencies";
import { trackAnalyticsEvent } from "../analytics/analytics";

const getSavedCurrency = () => {
  const savedCurrency = localStorage.getItem("user_currency") || "EUR";
  if (isSupportedCurrency(savedCurrency)) return savedCurrency.toUpperCase();

  localStorage.setItem("user_currency", "EUR");
  return "EUR";
};

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userId, setUserId] = useState(-1);
  const [token, setToken] = useState(() => localStorage.getItem("token") || null);
  const [firebaseToken, setFirebaseToken] = useState(() => localStorage.getItem("firebaseToken") || null);
  const [role, setRole] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [walletCurrency, setWalletCurrency] = useState(() => {
    if (typeof window !== "undefined") {
      return getSavedCurrency();
    }
    return "EUR";
  });

  const [twoFactorMethod, setTwoFactorMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverExpMs, setServerExpMs] = useState(null);

  const refreshWallets = useCallback(async () => {
    if (!token) return;
    try {
      const walletData = await WalletService.getUserWallet();
      setWallet(walletData);
      if (walletData?.currency) {
        setWalletCurrency(walletData.currency);
        localStorage.setItem("user_currency", walletData.currency);
      }
      return walletData;
    } catch (err) {
      console.error("Failed to refresh wallet:", err);
    }
  }, [token]);

  const changeCurrency = async (newCurrency) => {
    if (!isSupportedCurrency(newCurrency)) return;
    newCurrency = newCurrency.toUpperCase();
    setWalletCurrency(newCurrency);
    localStorage.setItem("user_currency", newCurrency);

    if (token && wallet?.id) {
      try {
        await WalletService.changeWalletCurrency(wallet.id, newCurrency);
        await refreshWallets();
      } catch (err) {
        console.error("Failed to update wallet currency on server:", err);
      }
    }
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  useEffect(() => {
    if (firebaseToken) {
      localStorage.setItem("firebaseToken", firebaseToken);
    } else {
      localStorage.removeItem("firebaseToken");
    }
  }, [firebaseToken]);

  useEffect(() => {
    const verifyToken = async () => {
      const savedCurrency = getSavedCurrency();

      if (!token) {
        setUserId(-1);
        trackAnalyticsEvent.setUserId(null);
        setRole(null);
        setTwoFactorMethod(null);
        setWallet(null);
        setWalletCurrency(savedCurrency);
        setLoading(false);
        return;
      }

      try {
        const response = await AuthenticationService.checkToken();
        const activeUserId = response.user.email || response.user.id || -1;
        setUserId(activeUserId);
        trackAnalyticsEvent.setUserId(activeUserId);
        setRole(response.user.role || null);
        setTwoFactorMethod(response.user.two_factor_method || null);
        if (Number.isFinite(response.expires_in_seconds)) {
          setServerExpMs(Date.now() + response.expires_in_seconds * 1000);
        }

        const walletData = await WalletService.getUserWallet();
        setWallet(walletData);

        const activeCur = walletData?.currency || savedCurrency || "EUR";
        setWalletCurrency(activeCur);
        localStorage.setItem("user_currency", activeCur);
      } catch (error) {
        console.error("Token check failed:", error);
        setToken(null);
        setUserId(-1);
        trackAnalyticsEvent.setUserId(null);
        setRole(null);
        setTwoFactorMethod(null);
        setWallet(null);
        setWalletCurrency(savedCurrency);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    let expMs = serverExpMs;
    if (!Number.isFinite(expMs)) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        expMs = payload.exp * 1000;
      } catch {
        return;
      }
    }
    if (!Number.isFinite(expMs)) return;

    let timerId;
    const logout = () => {
      localStorage.removeItem("token");
      const pathLang = window.location.pathname.split("/").filter(Boolean)[0];
      const lang = pathLang || localStorage.getItem("i18nextLng") || "en";
      window.location.href = `/${lang}/sign-in`;
    };
    const check = () => {
      clearTimeout(timerId);
      const remaining = expMs - Date.now();
      if (remaining <= 0) return logout();
      // setTimeout overflows above ~24.8 days; re-check periodically
      timerId = setTimeout(check, Math.min(remaining, 2 ** 31 - 1));
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [token, serverExpMs]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        firebaseToken,
        setFirebaseToken,
        userId,
        setUserId,
        role,
        setRole,
        wallet,
        setWallet,
        walletCurrency,
        setWalletCurrency,
        changeCurrency,
        refreshWallets,
        loading,
        twoFactorMethod,
        setTwoFactorMethod,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
